import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Coins, Swords, Trophy, Timer, Crown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { card, label, btnPrimary, btnSecondary } from "@/lib/styles";

const fmtTime = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export default function Gauntlet() {
  const { user, setUser } = useAuth();
  const [board, setBoard] = useState(null);
  const [view, setView] = useState("lobby"); // lobby | run | result
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);

  const loadBoard = useCallback(async () => {
    try {
      const { data } = await api.get("/gauntlet");
      setBoard(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  useEffect(() => {
    if (view !== "run") return;
    const id = setInterval(() => setElapsed(Date.now() - startRef.current), 100);
    return () => clearInterval(id);
  }, [view]);

  if (!user) return null;
  const gold = user.gold ?? 0;

  const enter = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gauntlet/enter");
      setQs(data.questions);
      setAnswers(Array(data.questions.length).fill(-1));
      setUser((u) => ({ ...u, gold: data.gold }));
      startRef.current = Date.now() - (data.elapsed_ms || 0); // server clock is truth; resume keeps ticking
      setElapsed(data.elapsed_ms || 0);
      if (data.resumed) toast.info("Your run was still open. The clock never stopped.");
      setView("run");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gauntlet/submit", { answers, elapsed_ms: Date.now() - startRef.current });
      setResult(data);
      setUser((u) => ({ ...u, xp: data.xp }));
      setView("result");
      loadBoard();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- RUN ----
  if (view === "run") {
    const answeredAll = answers.every((a) => a >= 0);
    return (
      <div className="min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between">
            <span className={label}>the gauntlet</span>
            <span className="inline-flex items-center gap-1 text-sm font-mono font-bold text-gold-light" data-testid="gauntlet-timer">
              <Timer size={15} /> {fmtTime(elapsed)}
            </span>
          </div>
          <div className="mt-6 space-y-5">
            {qs.map((q, i) => (
              <div key={i} className={`${card} p-5`}>
                <p className="font-medium text-zinc-100"><span className="text-gold">{i + 1}.</span> {q.q}</p>
                <div className="mt-3 grid gap-2">
                  {q.options.map((opt, j) => (
                    <button
                      key={j}
                      data-testid={`gauntlet-q${i}-opt${j}`}
                      onClick={() => setAnswers((a) => a.map((v, k) => (k === i ? j : v)))}
                      className={`text-left text-sm border rounded-md px-3 py-2 transition-all ${answers[i] === j ? "border-gold/70 bg-gold/10 text-gold-light" : "border-zinc-700 text-zinc-300 hover:border-zinc-600"}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="gauntlet-submit" onClick={submit} disabled={busy || !answeredAll} className={`${btnPrimary} mt-6 w-full`}>
            {answeredAll ? "Seal your answers" : "Answer every question to submit"}
          </button>
        </div>
      </div>
    );
  }

  // ---- RESULT ----
  if (view === "result" && result) {
    return (
      <div className="min-h-screen">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Swords className="mx-auto text-gold" size={40} />
          <h1 className="mt-4 text-4xl font-bold tracking-tight" data-testid="gauntlet-score">{result.correct}/{result.total}</h1>
          <p className="text-sm text-zinc-400 mt-1">+{result.xp_earned} XP earned</p>
          <p className="text-xs text-zinc-500 mt-4">Your run is on this month's board. One run each: top score, fastest time, claims the jackpot when the month turns.</p>
          <div className="mt-8 flex justify-center">
            <button onClick={() => setView("lobby")} className={btnPrimary} data-testid="gauntlet-back">Back to lobby</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- LOBBY ----
  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between">
          <span className={label}>the gauntlet</span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gold border border-gold/30 bg-gold/5 px-2 py-1 rounded-md">
            <Coins size={13} /> your gold · {gold}
          </span>
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">The Gauntlet</h1>
        <p className="text-sm text-zinc-500 mt-1">One run per month. Ante up, face {board?.size ?? 10} hard questions on the last month in AI: releases, research, and news. Every ante feeds the monthly jackpot: top score takes the pot and a champion badge, speed breaks ties. The clock starts the moment you enter.</p>

        {board && (
          <>
            <div className={`${card} p-6 mt-8 text-center`}>
              <span className={label}>this month's jackpot</span>
              <p className="mt-1 text-5xl font-mono font-bold text-gold-light inline-flex items-center gap-2" data-testid="gauntlet-pool">
                <Coins size={34} /> {board.pool}
              </p>
              {board.last_winner && (
                <p className="text-[11px] text-zinc-500 mt-3 inline-flex items-center gap-1">
                  <Crown size={12} className="text-yellow-400" /> Last month: {board.last_winner.name} won {board.last_winner.prize} gold
                </p>
              )}
              <div className="mt-5">
                <button data-testid="gauntlet-enter" onClick={enter} disabled={busy || !!board.my_best || gold < board.entry} className={btnPrimary}>
                  <Swords size={16} /> {busy ? "Altaris prepares the trial…" : board.my_best ? "Faced this month" : <>Enter · −{board.entry} gold</>}
                </button>
                {board.my_best ? (
                  <p className="text-[11px] text-zinc-500 mt-3">Your run: {board.my_best.score}/{board.my_best.total} · {fmtTime(board.my_best.elapsed_ms)}. Return when the month turns.</p>
                ) : (
                  gold < board.entry && <p className="text-[11px] text-rose-400/80 mt-2">Not enough gold to enter.</p>
                )}
              </div>
            </div>

            <div className={`${card} p-6 mt-6`}>
              <span className={label}><Trophy size={12} className="inline -mt-0.5" /> monthly leaderboard</span>
              {board.leaderboard.length === 0 ? (
                <p className="text-sm text-zinc-500 mt-3">No challengers yet this month. Be the first.</p>
              ) : (
                <div className="mt-3 divide-y divide-zinc-800">
                  {board.leaderboard.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`gauntlet-rank-${i}`}>
                      <span className="flex items-center gap-3">
                        <span className={`w-5 text-right font-mono ${i === 0 ? "text-gold-light" : "text-zinc-600"}`}>{i + 1}</span>
                        <span className={r.user_id === user.user_id ? "text-gold-light font-medium" : "text-zinc-300"}>{r.name}</span>
                      </span>
                      <span className="font-mono text-zinc-400">{r.score}/{r.total} · {fmtTime(r.elapsed_ms)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
